import React, { useEffect, useState } from "react";
import { CalendarCheck, CalendarSync, Loader2, Package } from "lucide-react";
import { DetailCard, SlimCard } from "../components/cards";
import { GridCard } from "../components/cards/gridCard";
import Row from "../components/general/row";
import Column from "../components/general/column";
import { Toaster, toast } from "react-hot-toast";
import { replenishPurchaseOrders, updateClient } from "../api/business-api";
import { fetchActiveLicenses, getClientByNetwork } from "../api/clients-api";
import { useNavigate } from "react-router-dom";
import Modal from "../components/general/modal";
import { Form } from "../components/general/form";
import { useAuth } from "../context/authContext";
import { SettingsCard } from "../components/cards/settingsCard";
import { formatCurrency } from "../utils";
import LicenseCard from "../components/cards/LicenseCard";

const covertToTwoDecimalPlace = (numberToConvert) => {
  const num = parseFloat(numberToConvert);
  if (isNaN(num)) {
    return "---";
  }
  return num.toFixed(2);
};

const formatDateTime = (dateTime) => {
  return new Date(dateTime).toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const Configuration = () => {
  const { user, loading, NETWORK, client, licensed, setClient } = useAuth();

  const navigate = useNavigate();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isRateConfigModalOpen, setIsRateConfigModalOpen] = useState(false);
  const [rateConfigFormData, setRateConfigFormData] = useState({});
  const [rateConfigLoading, setRateConfigLoading] = useState(false);
  const [isCreatePurchaseModalOpen, setIsCreatePurchaseModalOpen] =
    useState(false);
  const [createPurchaseFormData, setCreatePurchaseFormData] = useState({});
  const [autoFundRecovery, setAutoFundRecovery] = useState();
  const [autoFundRecoveryLoading, setAutoFundRecoveryLoading] = useState(false);
  const [activeLicense, setActiveLicense] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);

  useEffect(() => {
    setAutoFundRecovery(client?.is_fund_recovery_enabled);
  }, [client]);

  let loadingToastId = null;

  useEffect(() => {
    const fetchLatestClient = async () => {
      try {
        setClientLoading(true);
        const response = await getClientByNetwork(NETWORK);
        setClient(response?.data);
      } catch (error) {
        console.error("Error fetching latest client profile:", error);
      } finally {
        setClientLoading(false);
      }
    };

    if (NETWORK && NETWORK !== "unassigned") {
      fetchLatestClient();
    }
  }, [NETWORK, setClient]);

  useEffect(() => {
    const fetchLicenses = async () => {
      try {
        const response = await fetchActiveLicenses(client?.client_id);
        setActiveLicense(response?.data);
      } catch (error) {
        console.error("Error fetching active licenses:", error);
      }
    };

    if (client) fetchLicenses();
  }, [client]);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/");
  }, [user, loading, navigate]);

  const handleCreatePurchase = async () => {
    try {
      setButtonLoading(true);
      if (!loadingToastId) {
        loadingToastId = toast.loading("Loading...");
      }

      const newData = {
        external_token: createPurchaseFormData.external_token,
        amount_purchased: parseFloat(createPurchaseFormData.amount_purchased),
        unit_purchased: parseFloat(createPurchaseFormData.unit_purchased),
        network: NETWORK,
      };

      await replenishPurchaseOrders(newData);
      setCreatePurchaseFormData({
        external_token: "",
        amount_purchased: "",
        unit_purchased: "",
      });
      setIsCreatePurchaseModalOpen(false);
      toast.dismiss(loadingToastId);
      toast.success("Purchase order created successfully", { duration: 5000 });
    } catch (error) {
      console.error("Error saving purchase:", error);
      toast.dismiss(loadingToastId);
      toast.error("Failed to create purchase order.", { duration: 5000 });
    } finally {
      setButtonLoading(false);
    }
  };

  const handleRateConfigFormSubmit = async () => {
    try {
      setRateConfigLoading(true);
      if (!loadingToastId) {
        loadingToastId = toast.loading("Loading...");
      }

      const payload = { ...client };
      delete payload.commission;

      const data = {
        ...payload,
        rate_per_unit: parseFloat(rateConfigFormData.ratePerUnit) || 0,
      };
      await updateClient(data);

      toast.dismiss(loadingToastId);
      toast.success("Rate updated successfully.", { duration: 5000 });
      setClient((prevClient) => ({
        ...prevClient,
        rate_per_unit: data.rate_per_unit,
      }));
    } catch (error) {
      console.error("Error creating/updating rate config:", error);
      toast.dismiss(loadingToastId);
      toast.error("Failed to create/update rate.", { duration: 5000 });
    } finally {
      setRateConfigLoading(false);
    }
  };

  const handleAutoFundRecovery = async (isEnabled) => {
    setAutoFundRecoveryLoading(true);
    try {
      const data = {
        ...client,
        is_fund_recovery_enabled: isEnabled,
      };
      await updateClient(data);
      toast.success(
        `Auto fund recovery ${isEnabled ? "enabled" : "disabled"} successfully.`,
        { duration: 5000 },
      );
      setAutoFundRecovery(!autoFundRecovery);
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setAutoFundRecoveryLoading(false);
    }
  };

  const slimCardData = [
    {
      title: "Inventory",
      value: `${client?.inventory ? covertToTwoDecimalPlace(client?.inventory) + " kWh" : "N/A"}`,
      icon: Package,
      bgColor: "bg-primary-light",
    },
    {
      title: "Last replenished",
      value: `${client?.last_replenished ? formatDateTime(client?.last_replenished) : "-"}`,
      icon: CalendarSync,
      bgColor: "bg-primary-light",
    },
    {
      title: "Date created",
      value: `${client?.created_at ? formatDateTime(client?.created_at) : "-"}`,
      icon: CalendarCheck,
      bgColor: "bg-primary-light",
    },
  ];

  const rateConfigFormFields = [
    {
      type: "text",
      name: "ratePerUnit",
      label: "Rate",
      placeholder: "Enter rate",
      value: rateConfigFormData.ratePerUnit,
      required: true,
    },
  ];

  const createPurchaseFormFields = [
    {
      type: "text",
      name: "external_token",
      label: "External Token",
      placeholder: "Enter external token",
      value: createPurchaseFormData.external_token,
      required: true,
    },
    {
      type: "text",
      name: "amount_purchased",
      label: "Amount Purchased",
      placeholder: "Enter amount purchased",
      value: createPurchaseFormData.amount_purchased,
      required: true,
    },
    {
      type: "text",
      name: "unit_purchased",
      label: "Unit Purchased",
      placeholder: "Enter unit purchased",
      value: createPurchaseFormData.unit_purchased,
      required: true,
    },
  ];

  return (
    <Column>
      <Row>
        <Column className="w-full sm:w-full md:w-[100%] lg:w-[48%]">
          <LicenseCard license={activeLicense} validityStatus={licensed} />
          <GridCard
            title={"Inventory Details"}
            description={"Your inventory details"}
            loading={false}
            actions={[
              {
                label: "Create new purchase",
                onClick: () => setIsCreatePurchaseModalOpen(true),
              },
              {
                label: "Rate configuration",
                onClick: () => setIsRateConfigModalOpen(true),
              },
            ]}
          >
            {slimCardData.map((data, key) => {
              return (
                <SlimCard
                  key={key}
                  data={data}
                  loading={loading || clientLoading}
                />
              );
            })}
          </GridCard>
        </Column>

        <Column className="w-full sm:w-full md:w-[100%] lg:w-[50%]">
          {/* <LicenseCard license={activeLicense} /> */}
          {/* // In your Configuration component, update the DetailCard usage: */}

          <DetailCard
            title={"Profile details"}
            loading={loading || clientLoading}
            editableData={client}
            clientId={client?.client_id}
            setClient={setClient}
          >
            {/* PERSONAL DETAILS SECTION */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-primary-dark mb-3 uppercase tracking-wide">
                Personal Details
              </h3>
              <div className="flex items-center justify-between py-3 border-b border-lightBackground">
                <span className="text-subText font-nexa text-sm">
                  Full name
                </span>
                <span className="font-nexa text-primary-dark font-semibold">
                  {client?.name}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-lightBackground">
                <span className="text-subText font-nexa text-sm">Email</span>
                <span className="font-nexa text-primary-dark font-semibold">
                  {client?.email}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-subText font-nexa text-sm">Status</span>
                {client?.is_active ? (
                  <div className="flex items-center gap-2">
                    <span className="font-nexa text-primary-dark font-semibold">
                      Active
                    </span>
                    <div className="rounded-full bg-primary-normal h-2 w-2 animate-pulseGlow"></div>
                  </div>
                ) : (
                  <span className="font-nexa text-primary-error font-semibold">
                    Inactive
                  </span>
                )}
              </div>
            </div>

            {/* BILLING & RATES SECTION */}
            <div>
              <h3 className="text-sm font-semibold text-primary-dark mb-3 uppercase tracking-wide">
                Billing & Rates
              </h3>

              {/* Rate Per Unit */}
              <div className="flex items-center justify-between py-3 border-b border-lightBackground">
                <span className="text-subText font-nexa text-sm">
                  Rate Per Unit
                </span>
                <span className="font-nexa text-primary-dark font-semibold">
                  ₦{client?.rate_per_unit || 0}
                </span>
              </div>

              {/* Reorder Limit */}
              <div className="flex items-center justify-between py-3 border-b border-lightBackground">
                <span className="text-subText font-nexa text-sm">
                  Reorder Limit
                </span>
                <span className="font-nexa text-primary-dark font-semibold">
                  {client?.reorder_limit || 0} kWh
                </span>
              </div>

              {/* Min Amount */}
              <div className="flex items-center justify-between py-3 border-b border-lightBackground">
                <span className="text-subText font-nexa text-sm">
                  Min Amount
                </span>
                <span className="font-nexa text-primary-dark font-semibold">
                  ₦{client?.min_amount ?? "N/A"}
                </span>
              </div>

              {/* Max Amount */}
              <div className="flex items-center justify-between py-3">
                <span className="text-subText font-nexa text-sm">
                  Max Amount
                </span>
                <span className="font-nexa text-primary-dark font-semibold">
                  ₦{client?.max_amount ?? "N/A"}
                </span>
              </div>
            </div>

            {/* BANKING DETAILS SECTION */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-primary-dark mb-3 uppercase tracking-wide">
                Banking Details
              </h3>

              {/* Account Name */}
              <div className="flex items-center justify-between py-3 border-b border-lightBackground">
                <span className="text-subText font-nexa text-sm">
                  Account Name
                </span>
                <span className="font-nexa text-primary-dark font-semibold">
                  {client?.account_name || "N/A"}
                </span>
              </div>

              {/* Account Number */}
              <div className="flex items-center justify-between py-3 border-b border-lightBackground">
                <span className="text-subText font-nexa text-sm">
                  Account Number
                </span>
                <span className="font-nexa text-primary-dark font-semibold">
                  {client?.account_number || "N/A"}
                </span>
              </div>

              {/* Bank Name */}
              <div className="flex items-center justify-between py-3 border-b border-lightBackground">
                <span className="text-subText font-nexa text-sm">
                  Bank Name
                </span>
                <span className="font-nexa text-primary-dark font-semibold">
                  {client?.bank_name || "N/A"}
                </span>
              </div>

              {/* Payment Gateway */}
              <div className="flex items-center justify-between py-3">
                <span className="text-subText font-nexa text-sm">
                  Payment Gateway
                </span>
                <span className="font-nexa text-primary-dark font-semibold">
                  {client?.payment_gateway || "N/A"}
                </span>
              </div>
            </div>

            {/* NETWORK & SYSTEM SECTION */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-primary-dark mb-3 uppercase tracking-wide">
                System information
              </h3>

              {/* <div className="flex items-center justify-between py-3 border-b border-lightBackground">
                <span className="text-subText font-nexa text-sm">Network</span>
                <span className="font-nexa text-primary-dark font-semibold">{client?.network}</span>
              </div> */}

              {/* Color Scheme */}
              <div className="flex items-center justify-between py-3">
                <span className="text-subText font-nexa text-sm">
                  Color Scheme
                </span>
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-full border-2 border-gray-300"
                    style={{
                      backgroundColor:
                        client?.color_scheme === "orange"
                          ? "#ff8c00"
                          : client?.color_scheme === "blue"
                            ? "#3b82f6"
                            : client?.color_scheme === "green"
                              ? "#10b981"
                              : client?.color_scheme === "purple"
                                ? "#8b5cf6"
                                : client?.color_scheme === "red"
                                  ? "#ef4444"
                                  : "#6b7280",
                    }}
                  ></div>
                  <span className="font-nexa text-primary-dark font-semibold capitalize">
                    {client?.color_scheme}
                  </span>
                </div>
              </div>
            </div>
          </DetailCard>
          {/* <DetailCard title={"Profile details"} loading={loading}>
            <div className="flex items-center justify-between py-3 border-b border-lightBackground">
              <span className="text-subText font-nexa text-sm">Full name</span>
              <span className="font-nexa text-primary-dark font-semibold">{client?.name}</span>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-lightBackground">
              <span className="text-subText font-nexa text-sm">Status</span>
              {client?.active ? (
                <div className="flex items-center gap-2">
                  <span className="font-nexa text-primary-dark font-semibold">Active</span>
                  <div className="rounded-full bg-primary-normal h-2 w-2 animate-pulseGlow"></div>
                </div>
              ) : (
                <span className="font-nexa text-primary-error font-semibold">Inactive</span>
              )}
            </div>

            <div className="flex items-center justify-between py-3">
              <span className="text-subText font-nexa text-sm">Network</span>
              <span className="font-nexa text-primary-dark font-semibold">{client?.network}</span>
            </div>
          </DetailCard> */}

          <SettingsCard
            title={"Settings"}
            loading={loading}
            customWidth={"w-full sm:w-full md:w-full lg:w-full min-h-96"}
          >
            <div className="flex flex-row justify-between items-center py-3 px-4 border border-subText shadow-md shadow-lightGray rounded-lg">
              <div className="flex-1">
                <p className="m-0 p-0 text-primary-dark font-medium text-md">
                  Auto fund recovery
                </p>
                <span className="text-xs text-gray-600 leading-tight">
                  Automatically charges tenants to recover unpaid bills.
                </span>
              </div>

              <div
                className={`relative flex items-center h-7 w-14 rounded-full px-1 transition-colors duration-300 cursor-pointer shrink-0 ${
                  autoFundRecovery ? "bg-primaryDull" : "bg-error"
                } ${!licensed ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"}`}
                onClick={() => {
                  licensed
                    ? handleAutoFundRecovery(!autoFundRecovery)
                    : toast.error(
                        "You need a valid license to enable this feature.",
                      );
                }}
              >
                {autoFundRecoveryLoading ? (
                  <span className="h-5 w-5 rounded-full bg-white flex items-center justify-center">
                    <Loader2
                      className="animate-spin text-primary-gray"
                      size={14}
                    />
                  </span>
                ) : (
                  <span
                    className={`h-5 w-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                      autoFundRecovery ? "translate-x-7" : "translate-x-0"
                    }`}
                  />
                )}
              </div>
            </div>
          </SettingsCard>
        </Column>
      </Row>

      {/* Rate config modal */}
      <Modal
        isOpen={isRateConfigModalOpen}
        onClose={() => setIsRateConfigModalOpen(false)}
        title="Rate configs"
      >
        <Form
          fields={rateConfigFormFields}
          onSubmit={handleRateConfigFormSubmit}
          formData={rateConfigFormData}
          setFormData={setRateConfigFormData}
          isLoading={rateConfigLoading}
          btnText="Create/Update"
        />

        <div className="p-2 rounded-lg bg-lightBackground mt-3">
          <p className="text-sm text-primary-dark font-nexa m-0">
            Current rate: {formatCurrency(client?.rate_per_unit) || 0} per unit
          </p>
        </div>
      </Modal>

      {/* Load token Modal */}
      <Modal
        isOpen={isCreatePurchaseModalOpen}
        onClose={() => setIsCreatePurchaseModalOpen(false)}
        title="Create purchase"
      >
        <Form
          fields={createPurchaseFormFields}
          onSubmit={handleCreatePurchase}
          formData={createPurchaseFormData}
          setFormData={setCreatePurchaseFormData}
          isLoading={buttonLoading}
          btnText="Create"
        />
      </Modal>

      <Toaster position="top-center" />
    </Column>
  );
};

export default Configuration;
